/* eslint-disable no-unused-vars */
import React, { FocusEventHandler, MouseEventHandler, useEffect, useRef } from 'react';

import { withGoogleAnalyticsClickEvent, GoogleAnalyticsConstants } from '@vvoyages/google-analytics';

import classNames from 'classnames';

import styles from './SignInInput.module.scss';

const INPUT_DEFAULT_BACKGROUND = 'rgb(255, 255, 255)';

export type SignInPropType = {
  label: string;
  type: string;
  value: string;
  required: boolean;
  disabled: boolean;
  onChange: Function;
  onClick: MouseEventHandler<HTMLInputElement>;
  error: false;
  name: string;
  onAutoFill: Function;
  autoFocus: boolean;
  onBlur: FocusEventHandler<HTMLInputElement>;
};

export interface SignInInputProps {
  label?: string;
  type?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange?(...args: unknown[]): unknown;
  onClick?(...args: unknown[]): unknown;
  error?: boolean;
  name?: string;
  onAutoFill?(...args: unknown[]): unknown;
  onBlur?(...args: unknown[]): unknown;
}

const SignInInput = ({
  label = '',
  type = '',
  value,
  required = false,
  disabled = false,
  onChange = () => {},
  onClick = () => {},
  error = false,
  name = '',
  onAutoFill = () => {},
  autoFocus = false,
  onBlur = () => {},
}: SignInInputProps) => {
  const inputRef: any = useRef(null);

  useEffect(() => {
    const testWebkitAutofill = () => {
      const a = window.getComputedStyle(inputRef?.current);
      if (a.getPropertyValue('background-color') !== INPUT_DEFAULT_BACKGROUND) {
        onAutoFill(true);
      }
    };

    setTimeout(testWebkitAutofill, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoFocus) {
      inputRef?.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnimationStart = (event: any) => {
    if ([styles.onAutoFillStart, styles.onAutoFillCancel].includes(event.animationName)) {
      onAutoFill(true);
    }
  };

  const changeHandler = (e: any) => {
    onChange(e.target.value, inputRef?.current?.validationMessage);
  };

  return (
    <div className={styles.input}>
      <div className={styles['relative-input']}>
        <label htmlFor={name} className={styles.visuallyhidden}>
          {`${label}${required ? '*' : ''}`}
        </label>
        <input
          ref={inputRef}
          id={name}
          name={name}
          value={value}
          type={type}
          onChange={changeHandler}
          onBlur={onBlur}
          placeholder=" "
          required={required}
          disabled={disabled}
          className={classNames(styles['input-section'], {
            [styles['input-error']]: error,
          })}
          autoComplete="off"
          onClick={onClick}
          onAnimationStart={handleAnimationStart}
          onAnimationEnd={handleAnimationStart}
        />
        <span className={styles.input__label} aria-hidden="true">
          {`${label}${required ? '*' : ''}`}
        </span>
      </div>
    </div>
  );
};

export default withGoogleAnalyticsClickEvent(
  SignInInput,
  () => GoogleAnalyticsConstants.EVENT_ACTION.FIELD_CLICK,
  (props: { name: string }) => {
    if (props.name === 'email address') {
      return 'email';
    }
    return props.name;
  },
);
